import { AccordionButton, AccordionItem, AccordionPanel } from '@components/Accordion/Accordion';
import SuggestionsSection, {
  useSuggestionsSection,
} from '@components/Card/FieldEditPopovers/SuggestionsSection/SuggestionsSection';
import Checkbox from '@components/Checkbox/Checkbox';
// types and consts
import {
  CONTACT_FIELD_ICON_OPTIONS,
  ENABLE_CHANGING_ICONS,
  EmailSignatureContactDetailsFieldWidth,
} from '@components/EmailSignaturev2/consts';
import EmailSignatureIcon from '@components/EmailSignaturev2/EmailSignatureIcon';
import EmailSignatureSpanButton from '@components/EmailSignaturev2/EmailSignatureSpanButton';
import type {
  EmailSignatureContactDetailsField,
  EmailSignatureContactDetailsFieldId,
  EmailSignatureIconName,
  EmailSignatureV2,
} from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
import Input from '@components/Input/Input';
import PhoneNumberInput, {
  type OnChangeDetails,
  usePhoneNumberInput,
} from '@components/PhoneNumberInput/PhoneNumberInput';
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
// hooks and context
import { useUser } from '@context/user-context';
import { faPencil, faSortAlt, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import { getSuggestionsFromURLFieldValue } from '@modules/card-helpers';
// utils
import {
  formatPhoneNumber,
  getContactDetailFieldWidth,
  getDefaultTitleForWebsiteIfNoUserInput,
} from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
import { useAccordionItemContext } from '@reach/accordion';
import { capitalize } from 'es-toolkit';
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
import { getCountryCallingCode } from 'libphonenumber-js';
import { motion } from 'motion/react';
import {
  type FC,
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// components
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditableContactDetailsAccordionItem.module.scss';
import { EmailSignatureEditorContactAccordionContext } from './EmailSignatureEditorContactSection';

type EmailSignatureEditableAccordionItemProps = EmailSignatureContactDetailsField & {
  isLocked: boolean;
};

const EmailSignatureEditableAccordionItem: FC<
  React.PropsWithChildren<EmailSignatureEditableAccordionItemProps>
> = ({ id, label, value, baseIcon, isLocked }) => {
  const { panelIndex, registerNode } = useSideEditorContext();
  const [contactDetailAccordionNode, contactDetailAccordionNodeRef] = useNode<HTMLDivElement>();

  useEffect(() => {
    if (!contactDetailAccordionNode) return;
    registerNode(`CONTACT_DETAIL_ACCORDION_${id}`, {
      panelBelongTo: panelIndex,
      node: contactDetailAccordionNode,
    });
  }, [contactDetailAccordionNode, id, panelIndex, registerNode]);

  return (
    <motion.div
      ref={contactDetailAccordionNodeRef}
      key={id}
      layout="position"
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className={styles['accordion-container']}
    >
      <AccordionItem>
        <EmailSignatureEditableAccordionHeader
          fieldId={id}
          icon={baseIcon}
          label={label}
          value={value}
          isLocked={isLocked}
        />
        <EmailSignatureEditableAccordionDivider />
        <EmailSignatureEditableAccordionPanel isLocked={isLocked} fieldId={id} />
      </AccordionItem>
    </motion.div>
  );
};

const EmailSignaturePhoneNumberInputs: FC<
  React.PropsWithChildren<{
    fieldId: EmailSignatureContactDetailsFieldId;
    enableExtension: boolean;
    isLocked: boolean;
  }>
> = ({ fieldId, enableExtension, isLocked }) => {
  const { user } = useUser();
  const { hasEditedFieldWidth, emailSignature, emailSignatureDispatch } = useContext(
    emailSignatureSideEditorServicesContext,
  );

  const [isFormattedAsInternationalNumber, setIsFormattedAsInternationalNumber] = useState(
    () =>
      emailSignature.contactDetails[fieldId].value.startsWith('+') ||
      isEmpty(emailSignature.contactDetails[fieldId].value),
  );
  const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);

  const { countryCode, setCountryCode, phoneNumber, setPhoneNumber } = usePhoneNumberInput(
    emailSignature.contactDetails[fieldId].value,
    emailSignature.contactDetails[fieldId].countryCode ?? user?.country,
    hasBeenEdited,
  );

  function changeContactDetailFieldWidth(
    phoneNumberUserInput: string,
    extension: string | undefined,
    emailSignature: EmailSignatureV2,
  ) {
    if (hasEditedFieldWidth) return;

    const phoneNumber = formatPhoneNumber(
      phoneNumberUserInput,
      emailSignature.contactDetails[fieldId].extension,
    );
    const newFieldWidth = getContactDetailFieldWidth(
      phoneNumber,
      emailSignature.design.font,
      emailSignature.design.contactDetailsDisplayIcon,
    );

    if (newFieldWidth !== emailSignature.contactDetails[fieldId].fieldWidth) {
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH',
        value: {
          fieldId,
          updatedFieldValue: newFieldWidth,
        },
      });
    }
  }

  function handleIntlPhoneNumberChange(details: OnChangeDetails) {
    setHasBeenEdited(true);
    setPhoneNumber(details.number);
    setCountryCode(details.countryCode);
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_PHONE_NUMBER',
      value: {
        fieldId,
        updatedFieldValue: details.preservedNumber,
        countryCode: details.countryCode,
      },
    });

    changeContactDetailFieldWidth(
      details.preservedNumber,
      emailSignature.contactDetails[fieldId].extension,
      emailSignature,
    );
  }

  function handleLocalisedPhoneNumberChange(number: string) {
    setHasBeenEdited(true);
    setPhoneNumber(number);
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_PHONE_NUMBER',
      value: {
        fieldId,
        updatedFieldValue: number,
      },
    });
    changeContactDetailFieldWidth(
      number,
      emailSignature.contactDetails[fieldId].extension,
      emailSignature,
    );
  }

  function handlePhoneNumberFormatChange(isInternational: boolean) {
    setHasBeenEdited(true);
    if (!isInternational) {
      handleLocalisedPhoneNumberChange(phoneNumber);
    } else {
      const countryCallingCode = getCountryCallingCode(countryCode || 'US');
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_PHONE_NUMBER',
        value: {
          fieldId,
          updatedFieldValue: `+${countryCallingCode}${phoneNumber}`,
          countryCode: countryCode || 'US',
        },
      });
    }
    setIsFormattedAsInternationalNumber(isInternational);
  }

  function handleExtensionChange(extension: string) {
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_EXTENSION',
      value: {
        fieldId,
        updatedFieldValue: extension,
      },
    });

    changeContactDetailFieldWidth(
      emailSignature.contactDetails[fieldId].value,
      extension,
      emailSignature,
    );
  }

  return (
    <>
      <div className={styles['contact-detail-field']}>
        <div className={styles['contact-detail-field-input']}>
          <div className={styles['phone-number-container']}>
            <div className={styles['phone-number-input']}>
              {isFormattedAsInternationalNumber && (
                <PhoneNumberInput
                  disabled={isLocked}
                  phoneNumber={phoneNumber}
                  countryCode={countryCode}
                  onChange={handleIntlPhoneNumberChange}
                />
              )}

              {!isFormattedAsInternationalNumber && (
                <div className={styles['local-phone-number-container']}>
                  <input
                    disabled={isLocked}
                    type="tel"
                    autoComplete="tel-national"
                    className={styles['local-phone-number']}
                    value={phoneNumber}
                    onChange={(event) => handleLocalisedPhoneNumberChange(event.target.value)}
                  />
                </div>
              )}
            </div>
            {enableExtension && (
              <div className={styles['extension-input']}>
                <Input
                  disabled={isLocked}
                  value={emailSignature.contactDetails[fieldId].extension || ''}
                  onChange={handleExtensionChange}
                  contained
                  label="Ext."
                  hideAllErrorMessages
                />
              </div>
            )}
          </div>
          <div className={styles['phone-number-format-selector-container']}>
            <Checkbox
              disabled={isLocked}
              onChange={handlePhoneNumberFormatChange}
              checked={isFormattedAsInternationalNumber}
              label="Use international number"
              size="small"
              type="subtle"
            />
          </div>
        </div>
      </div>
      <div className={styles['contact-detail-field']} data-type={'input'}>
        <div className={styles['contact-detail-field-input']}>
          <Input
            disabled={isLocked}
            value={emailSignature.contactDetails[fieldId].label}
            onChange={(newText) =>
              emailSignatureDispatch({
                type: 'UPDATE_CONTACT_DETAIL_FIELD_LABEL',
                value: { fieldId, updatedFieldValue: newText },
              })
            }
            label={'Label (optional)'}
            enableClearButton={!isLocked}
            hideAllErrorMessages
            contained
          />
        </div>
      </div>
    </>
  );
};

type EmailSignatureWebsiteInputsProps = {
  fieldId: EmailSignatureContactDetailsFieldId;
  isLocked: boolean;
};

const EmailSignatureWebsiteInputs: FC<
  React.PropsWithChildren<EmailSignatureWebsiteInputsProps>
> = ({ fieldId, isLocked }) => {
  const { emailSignature, emailSignatureDispatch, hasEditedFieldWidth } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const suggestions = useMemo(() => {
    const derivedSuggestions = getSuggestionsFromURLFieldValue(
      'link',
      emailSignature.contactDetails[fieldId].value,
    );
    return [...derivedSuggestions];
  }, [emailSignature.contactDetails, fieldId]);
  const [selectedOption, setSelectedOption] = useSuggestionsSection(
    emailSignature.contactDetails[fieldId].title || '',
    suggestions,
  );

  function changeContactDetailFieldWidth(url: string, title?: string) {
    if (hasEditedFieldWidth) return;

    const actualTitleToDisplay = title || getDefaultTitleForWebsiteIfNoUserInput(url);
    const newFieldWidth = getContactDetailFieldWidth(
      actualTitleToDisplay,
      emailSignature.design.font,
      emailSignature.design.contactDetailsDisplayIcon,
    );

    if (newFieldWidth !== emailSignature.contactDetails[fieldId].fieldWidth) {
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH',
        value: {
          fieldId,
          updatedFieldValue: newFieldWidth,
        },
      });
    }
  }

  function handleValueChange(value: string) {
    changeContactDetailFieldWidth(value, emailSignature.contactDetails[fieldId].title);
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_VALUE',
      value: {
        fieldId,
        updatedFieldValue: value,
      },
    });
  }

  function handleTitleChange(title: string) {
    changeContactDetailFieldWidth(emailSignature.contactDetails[fieldId].value, title);
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_TITLE',
      value: {
        fieldId,
        updatedFieldValue: title,
      },
    });
  }

  function handleSelectSuggestion(suggestion: string) {
    const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

    setSelectedOption(newSelectedOption);
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_TITLE',
      value: {
        fieldId,
        updatedFieldValue: newSelectedOption,
      },
    });
  }

  return (
    <>
      <div className={styles['contact-detail-field']} data-type={'input'}>
        <div className={styles['contact-detail-field-input']}>
          <Input
            disabled={isLocked}
            value={emailSignature.contactDetails[fieldId].value}
            onChange={handleValueChange}
            contained
            label={'URL'}
            hideAllErrorMessages
          />
        </div>
      </div>
      <div className={styles['contact-detail-field']} data-type={'input'}>
        <div className={styles['contact-detail-field-input']}>
          <Input
            disabled={isLocked}
            value={emailSignature.contactDetails[fieldId].title ?? ''}
            onChange={handleTitleChange}
            contained
            label={'Title'}
            hideAllErrorMessages
          />

          {suggestions.length > 0 && !isLocked && (
            <div className={styles['suggestions-section-container']}>
              <SuggestionsSection
                options={suggestions}
                onSelect={handleSelectSuggestion}
                selectedOption={selectedOption}
                subject="title"
              />
            </div>
          )}
        </div>
      </div>
      {!emailSignature.design.contactDetailsDisplayIcon && (
        <div className={styles['contact-detail-field']} data-type={'input'}>
          <div className={styles['contact-detail-field-input']}>
            <Input
              disabled={isLocked}
              value={emailSignature.contactDetails[fieldId].label}
              onChange={(newText) =>
                emailSignatureDispatch({
                  type: 'UPDATE_CONTACT_DETAIL_FIELD_LABEL',
                  value: { fieldId, updatedFieldValue: newText },
                })
              }
              enableClearButton={!isLocked}
              label={'Label (optional)'}
              contained
              hideAllErrorMessages
              size="small"
            />
          </div>
        </div>
      )}
    </>
  );
};

const EmailSignatureBasicInputs: FC<
  React.PropsWithChildren<{
    fieldId: EmailSignatureContactDetailsFieldId;
    labelForValueField: string;
    isLocked: boolean;
  }>
> = ({ fieldId, labelForValueField, isLocked }) => {
  const { emailSignature, emailSignatureDispatch, hasEditedFieldWidth } = useContext(
    emailSignatureSideEditorServicesContext,
  );

  function changeContactDetailFieldWidth(newText: string) {
    if (hasEditedFieldWidth) return;

    const newFieldWidth = getContactDetailFieldWidth(
      newText,
      emailSignature.design.font,
      emailSignature.design.contactDetailsDisplayIcon,
    );

    if (newFieldWidth !== emailSignature.contactDetails[fieldId].fieldWidth) {
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH',
        value: {
          fieldId,
          updatedFieldValue: newFieldWidth,
        },
      });
    }
  }

  function handleValueChange(newText: string) {
    changeContactDetailFieldWidth(newText);
    emailSignatureDispatch({
      type: 'UPDATE_CONTACT_DETAIL_FIELD_VALUE',
      value: {
        fieldId,
        updatedFieldValue: newText,
      },
    });
  }

  return (
    <>
      <div className={styles['contact-detail-field']} data-type={'input'}>
        <div className={styles['contact-detail-field-input']}>
          <Input
            disabled={isLocked}
            value={emailSignature.contactDetails[fieldId].value}
            onChange={handleValueChange}
            label={labelForValueField}
            contained
            hideAllErrorMessages
            enableClearButton={!isLocked}
          />
        </div>
      </div>
      <div className={styles['contact-detail-field']} data-type={'input'}>
        <div className={styles['contact-detail-field-input']}>
          <Input
            disabled={isLocked}
            value={emailSignature.contactDetails[fieldId].label}
            onChange={(newText) =>
              emailSignatureDispatch({
                type: 'UPDATE_CONTACT_DETAIL_FIELD_LABEL',
                value: { fieldId, updatedFieldValue: newText },
              })
            }
            label={'Label (optional)'}
            contained
            hideAllErrorMessages
            enableClearButton={!isLocked}
          />
        </div>
      </div>
    </>
  );
};

type EmailSignatureEditableAccordionPanelProps = {
  fieldId: EmailSignatureContactDetailsFieldId;
  isLocked: boolean;
};

const EmailSignatureEditableAccordionPanel: FC<
  React.PropsWithChildren<EmailSignatureEditableAccordionPanelProps>
> = ({ fieldId, isLocked }) => {
  const { emailSignature, emailSignatureDispatch, onEditFieldWidth } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const outlineTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  function getInputsComponent(): ReactElement {
    const contactDetailsFieldType = emailSignature.contactDetails[fieldId].type;
    if (
      contactDetailsFieldType === 'phone' ||
      contactDetailsFieldType === 'whatsapp' ||
      contactDetailsFieldType === 'signal'
    ) {
      return (
        <EmailSignaturePhoneNumberInputs
          fieldId={fieldId}
          enableExtension={contactDetailsFieldType === 'phone'}
          isLocked={isLocked}
        />
      );
    } else if (contactDetailsFieldType === 'website') {
      return <EmailSignatureWebsiteInputs fieldId={fieldId} isLocked={isLocked} />;
    } else
      return (
        <EmailSignatureBasicInputs
          isLocked={isLocked}
          fieldId={fieldId}
          labelForValueField={
            contactDetailsFieldType === 'skype' ? 'Username' : capitalize(contactDetailsFieldType)
          }
        />
      );
  }

  return (
    <AccordionPanel>
      <div className={styles['email-signature-editor-accordion-panel']}>
        {getInputsComponent()}

        <div className={styles['contact-detail-field']}>
          <div className={styles['contact-detail-field-label']}>Field width</div>
          <div className={styles['contact-detail-field-input']}>
            <FormSelectorGroup
              className={styles['form-selector-group']}
              selectedValue={emailSignature.contactDetails[fieldId].fieldWidth}
              onChange={(newValue) => {
                // Make sure quickly togglng the options doesn't cause an outline to be prematurely canceled
                if (outlineTimeoutRef.current) {
                  clearTimeout(outlineTimeoutRef.current);
                  outlineTimeoutRef.current = undefined;
                }

                emailSignatureDispatch({
                  type: 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH',
                  value: {
                    fieldId,
                    updatedFieldValue: newValue as EmailSignatureContactDetailsFieldWidth,
                  },
                });
                onEditFieldWidth();

                // Show the outline for 1.5 seconds to give the user a better idea of what size the field is
                Emitter.emit('TURN_ON_FIELD_WIDTH_OUTLINE', fieldId);
                outlineTimeoutRef.current = setTimeout(() => {
                  Emitter.emit('TURN_OFF_FIELD_WIDTH_OUTLINE', fieldId);
                }, 1500);
              }}
              size="sm"
            >
              <FormSelector
                disabled={isLocked}
                value={EmailSignatureContactDetailsFieldWidth.HALF_WIDTH}
              >
                Half-width
              </FormSelector>

              <FormSelector
                disabled={isLocked}
                value={EmailSignatureContactDetailsFieldWidth.FULL_WIDTH}
              >
                Full-width
              </FormSelector>
            </FormSelectorGroup>
          </div>
        </div>
        {emailSignature.design.contactDetailsDisplayIcon &&
          ENABLE_CHANGING_ICONS &&
          CONTACT_FIELD_ICON_OPTIONS[emailSignature.contactDetails[fieldId].type].length > 1 && (
            <div className={styles['contact-detail-field']}>
              <div className={styles['contact-detail-field-label']}>Icon</div>
              <div className={styles['contact-detail-field-input']}>
                <FormSelectorGroup
                  className={styles['form-selector-icons-group']}
                  selectedValue={emailSignature.contactDetails[fieldId].baseIcon}
                  size="sm"
                  onChange={(newIcon) =>
                    emailSignatureDispatch({
                      type: 'UPDATE_CONTACT_DETAIL_FIELD_ICON',
                      value: {
                        fieldId,
                        updatedFieldValue: newIcon as EmailSignatureIconName,
                      },
                    })
                  }
                >
                  {CONTACT_FIELD_ICON_OPTIONS[emailSignature.contactDetails[fieldId].type].map(
                    (offeredIcon, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: historic
                      <div key={index} className={styles['icon-selector']}>
                        <FormSelector disabled={isLocked} value={offeredIcon}>
                          <EmailSignatureIcon icon={offeredIcon} size="1x"></EmailSignatureIcon>
                        </FormSelector>
                      </div>
                    ),
                  )}
                </FormSelectorGroup>
              </div>
            </div>
          )}
        {isLocked && (
          <EmailSignatureLockLayer
            message={
              'You cannot edit this field, please contact your admin if you have any questions'
            }
          />
        )}
      </div>
    </AccordionPanel>
  );
};

const EmailSignatureEditableAccordionDivider: FC<React.PropsWithChildren<unknown>> = () => {
  const { isExpanded } = useAccordionItemContext();
  return isExpanded ? <div className={styles['accordion-divider']}></div> : <></>;
};

const EmailSignatureEditableAccordionHeader: FC<
  React.PropsWithChildren<{
    fieldId: EmailSignatureContactDetailsFieldId;
    icon: EmailSignatureIconName;
    label: string;
    value: string;
    isLocked?: boolean;
  }>
> = ({ value, label, icon, fieldId, isLocked }) => {
  const { currentFieldIdToSwap, isInSwapMode, onEditActivate, onSwapActivate, onDeleteActivate } =
    useContext(EmailSignatureEditorContactAccordionContext);
  const { emailSignature } = useContext(emailSignatureSideEditorServicesContext);

  const valueToDisplay =
    emailSignature.contactDetails[fieldId].type === 'website'
      ? emailSignature.contactDetails[fieldId].title ||
        getDefaultTitleForWebsiteIfNoUserInput(value)
      : value;
  return (
    <AccordionButton className={styles['accordion-header']} headingLevel={1}>
      <span className={styles['accordion-header-title']}>
        <span className={styles['accordion-header-title-icon']}>
          <EmailSignatureIcon icon={icon} size="lg" />
        </span>
        <span className={styles['accordion-header-title-contents']}>
          <span className={styles['header-contents-title-value']}>{valueToDisplay} </span>
          <span className={styles['header-contents-title-label']}>{label}</span>
        </span>
      </span>

      {!isLocked && (
        <span className={styles['accordion-header-icons']}>
          {!isInSwapMode && (
            <>
              <EmailSignatureSpanButton onActivate={() => onEditActivate(fieldId)}>
                <FontAwesomeIcon icon={faPencil} size={'lg'} />
              </EmailSignatureSpanButton>
              <EmailSignatureSpanButton onActivate={() => onDeleteActivate(fieldId)}>
                <FontAwesomeIcon icon={faTrash} size={'lg'} />
              </EmailSignatureSpanButton>
            </>
          )}
          {emailSignature.contactDetailsOrder.length > 1 && (
            <EmailSignatureSpanButton onActivate={() => onSwapActivate(fieldId)}>
              {isInSwapMode && fieldId !== currentFieldIdToSwap ? (
                'Swap'
              ) : (
                <FontAwesomeIcon icon={faSortAlt} size={'lg'} />
              )}
            </EmailSignatureSpanButton>
          )}
        </span>
      )}
    </AccordionButton>
  );
};

export default EmailSignatureEditableAccordionItem;
